import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2, X, Upload, Save, ChevronUp, ChevronDown, Newspaper } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ArticlesAdminPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/articles`, { headers: getHeaders() });
      setArticles(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/api/admin/articles/${id}`, { headers: getHeaders() });
    setArticles(a => a.filter(x => x.id !== id));
    setDeleteConfirm(null);
  };

  const toggleStatus = async (a) => {
    const s = a.status === 'active' ? 'hidden' : 'active';
    await axios.put(`${API}/api/admin/articles/${a.id}`, { status: s }, { headers: getHeaders() });
    setArticles(prev => prev.map(x => x.id === a.id ? { ...x, status: s } : x));
  };

  const handleReorder = async (a, dir) => {
    const sorted = [...articles].sort((x, y) => x.order - y.order);
    const idx = sorted.findIndex(x => x.id === a.id);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= sorted.length) return;
    const other = sorted[swap];
    await Promise.all([
      axios.put(`${API}/api/admin/articles/${a.id}`, { order: other.order }, { headers: getHeaders() }),
      axios.put(`${API}/api/admin/articles/${other.id}`, { order: a.order }, { headers: getHeaders() }),
    ]);
    load();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="articles-admin-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Latest Articles</h1>
          <p className="text-sm text-slate-400 mt-1">Curate up to 3 articles shown on the homepage (under Products)</p>
        </div>
        <button
          data-testid="add-article-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm px-5 py-2.5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Article
        </button>
      </div>

      {articles.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
          <Newspaper className="w-12 h-12 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-400 text-sm">No articles yet. Add up to 3 featured articles to showcase on the homepage.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...articles].sort((a, b) => a.order - b.order).map(a => (
            <div key={a.id} data-testid={`article-row-${a.id}`} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => handleReorder(a, 'up')} className="p-0.5 text-slate-300 hover:text-slate-600"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => handleReorder(a, 'down')} className="p-0.5 text-slate-300 hover:text-slate-600"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="w-20 h-14 rounded-lg bg-slate-50 overflow-hidden flex-shrink-0">
                {a.image ? (
                  <img src={a.image.startsWith('http') ? a.image : `${API}/api/files/${a.image}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Newspaper className="w-5 h-5 text-slate-200" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700 truncate">{a.title_ar || 'Untitled'}</p>
                <p className="text-xs text-slate-400 truncate">{a.title_en || ''}</p>
              </div>
              {a.published_at && <span className="text-xs text-slate-400 hidden md:inline">{a.published_at}</span>}
              <button onClick={() => toggleStatus(a)} className={`px-3 py-1 rounded-full text-xs font-medium ${a.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                {a.status === 'active' ? 'Active' : 'Hidden'}
              </button>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(a); setShowForm(true); }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => setDeleteConfirm(a.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold mb-2">Delete Article?</h3>
            <p className="text-sm text-slate-500 mb-6">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200">Cancel</button>
              <button data-testid="confirm-delete-article-btn" onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <ArticleForm
          article={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
          nextOrder={articles.length}
        />
      )}
    </div>
  );
};

const ArticleForm = ({ article, onClose, onSaved, nextOrder }) => {
  const [tab, setTab] = useState('ar');
  const [form, setForm] = useState({
    title_ar: article?.title_ar || '',
    title_en: article?.title_en || '',
    excerpt_ar: article?.excerpt_ar || '',
    excerpt_en: article?.excerpt_en || '',
    image: article?.image || '',
    url: article?.url || '',
    published_at: article?.published_at || '',
    order: article?.order ?? nextOrder,
    status: article?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/admin/articles/upload`, fd, { headers: getHeaders() });
      setForm(p => ({ ...p, image: data.path }));
    } catch (err) {
      alert(`Upload failed: ${err.response?.data?.detail || err.message}`);
    }
    setUploading(false);
  };

  const isAr = tab === 'ar';
  const lk = isAr ? 'ar' : 'en';

  const handleSave = async () => {
    if (!form.title_ar.trim() && !form.title_en.trim()) return;
    setSaving(true);
    try {
      if (article) await axios.put(`${API}/api/admin/articles/${article.id}`, form, { headers: getHeaders() });
      else await axios.post(`${API}/api/admin/articles`, form, { headers: getHeaders() });
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">{article ? 'Edit Article' : 'Add Article'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Cover Image</label>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-200 rounded-xl aspect-[16/9] flex items-center justify-center cursor-pointer hover:border-blue-300 overflow-hidden"
            >
              {form.image ? (
                <img src={form.image.startsWith('http') ? form.image : `${API}/api/files/${form.image}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  {uploading ? <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto" /> : <Upload className="w-8 h-8 text-slate-300 mx-auto" />}
                  <p className="text-xs text-slate-400 mt-2">Click to upload (or paste image URL below)</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            <input
              value={form.image.startsWith('http') ? form.image : ''}
              onChange={e => setForm(p => ({ ...p, image: e.target.value }))}
              placeholder="Or paste a public image URL"
              className="mt-3 w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none"
            />
          </div>

          {/* Language Tabs */}
          <div className="flex gap-2 border-b border-slate-100 pb-1">
            <button onClick={() => setTab('ar')} className={`px-4 py-2 text-sm font-medium rounded-t-lg ${isAr ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-slate-400'}`}>Arabic</button>
            <button onClick={() => setTab('en')} className={`px-4 py-2 text-sm font-medium rounded-t-lg ${!isAr ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-slate-400'}`}>English</button>
          </div>

          <input
            data-testid="article-title-input"
            dir={isAr ? 'rtl' : 'ltr'}
            value={form[`title_${lk}`]}
            placeholder={`Title (${isAr ? 'Arabic' : 'English'}) *`}
            onChange={e => setForm(p => ({ ...p, [`title_${lk}`]: e.target.value }))}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none"
          />
          <textarea
            dir={isAr ? 'rtl' : 'ltr'}
            value={form[`excerpt_${lk}`]}
            placeholder={`Excerpt / Summary (${isAr ? 'Arabic' : 'English'})`}
            rows={3}
            onChange={e => setForm(p => ({ ...p, [`excerpt_${lk}`]: e.target.value }))}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none resize-none"
          />

          <div className="border-t border-slate-100 pt-4 space-y-4">
            <input
              value={form.url}
              onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
              placeholder="Article URL (e.g., https://linkedin.com/pulse/...)"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                value={form.published_at}
                onChange={e => setForm(p => ({ ...p, published_at: e.target.value }))}
                className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none"
              />
              <select
                value={form.status}
                onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none bg-white"
              >
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
            <input
              type="number"
              value={form.order}
              onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))}
              placeholder="Display Order"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none"
            />
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200">Cancel</button>
          <button
            data-testid="save-article-btn"
            onClick={handleSave}
            disabled={saving || (!form.title_ar.trim() && !form.title_en.trim())}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {article ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticlesAdminPage;
